import React from "react";
import { Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGameResponsive } from "@/hooks/use-game-responsive"; // Game responsive hook
import { GameRoom } from "@shared/schema";

interface GameState {
  players: Array<{ id: number; username: string }>;
  scores: { [key: number]: number };
  round: number;
  spectators?: number[];
  // Other properties omitted for brevity
}

interface GameScoreHeaderProps {
  gameRoom?: GameRoom | null;
  gameState: GameState;
  user: { id?: number } | null;
}

export function GameScoreHeader({ gameRoom, gameState, user }: Readonly<GameScoreHeaderProps>) {
  // Use game responsive hook
  const { 
    isMobileDevice: isMobile, 
    isTabletDevice: isTablet,
    isPortrait,
    isLandscape
  } = useGameResponsive();
  
  // Removing unused music muted state and effect since this is now handled in GameSettingsMenu
  
  // Determine text size based on screen and orientation
  const textSize = (() => {
    if (isMobile) return isPortrait ? "text-xs" : "text-2xs";
    if (isTablet) return isPortrait ? "text-sm" : "text-xs";
    return "text-base";
  })();
  
  // Determine icon size based on screen dimensions and orientation
  const iconSize = (() => {
    if (isMobile) return isPortrait ? "h-3 w-3" : "h-2.5 w-2.5";
    if (isTablet) return isPortrait ? "h-4 w-4" : "h-3.5 w-3.5";
    return "h-5 w-5";
  })();
  
  // Calculate indicator size based on device type
  let indicatorSize = "h-3 w-3"; // Default size
  if (isMobile) {
    indicatorSize = "h-2 w-2";
  } else if (isTablet) {
    indicatorSize = "h-2.5 w-2.5";
  }
  
  // Load music muted state from localStorage on component mount
  // (keeping the useEffect but removing the unused toggle function)
  
  return (
    <div className="flex justify-center ml-2">
      <div className={cn(
        "grid grid-cols-4 items-center bg-black/70 rounded-xl border border-primary/20 shadow-lg",
        isLandscape ? "scale-90" : "scale-100", // Apply scaling based on orientation
        "px-4 py-1.5" // Consistent padding all around
      )}>
        {/* Left section - Game Type */}
        <div className="col-span-1 flex justify-start items-center">
          <span className={cn("font-medium text-white/90 whitespace-nowrap", textSize)}>
            {gameRoom?.gameType === "ai" ? "Practice" : gameRoom?.gameType}
          </span>
        </div>
        
        {/* Center section - Score */}
        <div className="col-span-2 flex items-center justify-center px-3">
          {/* First player */}
          <div className="flex items-center">
            <span className={cn(
              textSize, 
              gameState.players[0]?.id === user?.id ? "text-primary" : "text-white/90"
            )}>
              {/* Show only first two characters of player name (capitalized) */}
              {gameState.players[0]?.username?.slice(0, 2).toUpperCase() || 'P1'}
            </span>
            <span className="mx-0.5"></span> {/* Smaller space between name and score */}
            <span className={cn(
              "font-bold", 
              textSize,
              gameState.players[0]?.id === user?.id ? "text-primary" : "text-white/90"
            )}>
              {gameState.scores[gameState.players[0]?.id] || 0}
            </span>
          </div>
          
          {/* Score separator */}
          <span className={cn("mx-2 font-medium text-white/70", textSize)}>-</span>
          
          {/* Second player */}
          <div className="flex items-center">
            <span className={cn(
              "font-bold", 
              textSize,
              gameState.players[1]?.id === user?.id ? "text-primary" : "text-white/90"
            )}>
              {gameState.scores[gameState.players[1]?.id] || 0}
            </span>
            <span className="mx-0.5"></span> {/* Smaller space between score and name */}
            <span className={cn(
              textSize, 
              gameState.players[1]?.id === user?.id ? "text-primary" : "text-white/90"
            )}>
              {/* Show only first two characters of player name (capitalized) */}
              {gameState.players[1]?.username?.slice(0, 2).toUpperCase() || 'P2'}
            </span>
          </div>
        </div>
        
        {/* Right section - Round indicator and spectator count */}
        <div className="col-span-1 flex items-center justify-end gap-3">
          {/* Spectator count (only shown when there are spectators) */}
          {(gameState.spectators?.length || 0) > 0 && (
            <div className="flex items-center">
              <Users className={cn(iconSize, "text-primary mr-1")} />
              <span className={cn("font-bold text-primary", textSize)}>
                {gameState.spectators?.length}
              </span>
            </div>
          )}
          
          {/* Round indicator */}
          <div className="flex items-center">
            <div className={cn(indicatorSize, "rounded-full bg-primary animate-pulse mr-1.5")}></div>
            <span className={cn("font-bold text-primary whitespace-nowrap", textSize)}>
              {isMobile && isLandscape ? gameState.round : `ROUND ${gameState.round}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
       
  
      
}
